import { useEffect, useId, useRef, useState } from 'react';
import { Building2, Check, ChevronUp } from 'lucide-react';
import { urlFile } from '../../AxiosConfig';
import { useAuthContext } from '../../contexts/AuthContext';
import { useEstabsContext } from '../../contexts/EstabsContext';
import './EstablishmentSwitcher.css';

// Logo do estabelecimento; sem logo (ou se falhar), a inicial do nome.
const EstabMark = ({ estab }: { estab?: any }) => {
    const [failed, setFailed] = useState(false);
    useEffect(() => setFailed(false), [estab?.imageId]);
    return (
        <span className="sf-estab-avatar" aria-hidden="true">
            {estab?.imageId && !failed
                ? <img src={`${urlFile}/${encodeURIComponent(estab.imageId)}`} alt="" onError={() => setFailed(true)} />
                : (estab?.name || 'E').trim().charAt(0).toLocaleUpperCase('pt-BR')}
        </span>
    );
};

export default function EstablishmentSwitcher({ expanded = true }: { expanded?: boolean }) {
    const { user } = useAuthContext();
    const { estabsUser, estabSession, switchEstab, isClientOnly, clientEstab, selectClientEstab } = useEstabsContext();
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLLIElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const panelId = useId();
    // Cliente troca apenas o estabelecimento exibido neste dispositivo (sem recarregar nem mudar a sessao).
    const establishments = isClientOnly
        ? estabsUser
        : estabsUser.filter((estab) => user?.root || estab.type !== 'client');
    const current = isClientOnly ? clientEstab : estabSession;

    useEffect(() => {
        if (!open) return;
        const onPointerDown = (event: PointerEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, [open]);

    // So faz sentido trocar com 2+ estabelecimentos (com um, o logo do menu ja indica qual e).
    const canSwitch = establishments.length > 1 && (isClientOnly || !!(user?.admin || user?.root || user?.worker));
    if (!canSwitch) return null;

    return (
        <li
            ref={containerRef}
            className="sf-estab-switcher"
            onBlur={(event) => {
                // Sem relatedTarget (toque em botao no iOS, teclado recolhido) o foco nao foi para fora: quem fecha e o pointerdown externo.
                if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false);
            }}
            onKeyDown={(event) => {
                if (event.key === 'Escape' && open) {
                    event.stopPropagation();
                    setOpen(false);
                    triggerRef.current?.focus();
                }
            }}
        >
            <button
                ref={triggerRef}
                type="button"
                className={`sf-estab-trigger ${expanded ? '' : 'is-collapsed'}`}
                aria-expanded={open}
                aria-controls={panelId}
                aria-label={`Trocar estabelecimento. Atual: ${current?.name || 'Nenhum'}`}
                title={!expanded ? current?.name : undefined}
                onClick={() => setOpen(!open)}
            >
                <span className="sf-estab-avatar" aria-hidden="true"><Building2 size={16} strokeWidth={1.75} /></span>
                {expanded && <>
                    <span className="sf-estab-trigger-copy">
                        <span className="sf-estab-current-name">{current?.name || 'Selecionar'}</span>
                    </span>
                    <ChevronUp size={14} strokeWidth={2} className="sf-estab-chevron shrink-0" aria-hidden="true" />
                </>}
            </button>
            {open && (
                <div id={panelId} className="sf-estab-popover" role="region" aria-label="Trocar estabelecimento">
                    <div className="sf-estab-popover-heading">Trocar estabelecimento</div>
                    <div className="sf-estab-options">
                        {establishments.map((estab) => {
                            const active = estab._id === current?._id;
                            return (
                                <button key={estab._id} type="button" className="sf-estab-option"
                                    aria-current={active ? 'true' : undefined}
                                    onClick={() => {
                                        setOpen(false);
                                        triggerRef.current?.focus();
                                        if (active) return;
                                        if (isClientOnly) selectClientEstab(estab._id);
                                        else switchEstab(estab._id);
                                    }}>
                                    <EstabMark estab={estab} />
                                    <span className="sf-estab-option-name">{estab.name}</span>
                                    {active && <Check size={15} strokeWidth={2.25} className="sf-estab-check shrink-0" aria-hidden="true" />}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </li>
    );
}
